// services/orderErrorHandler.service.ts
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToolsService } from '../../tools/tools.service';
import { DialogsService } from '../../manage/dialogs/dialogs.service';
import { ErrorActionId, ErrorConfig, OrderError } from 'src/app/models/interfaces/error.interfaces';
import { ErrorDialogComponent } from 'src/app/components/dialogs/fulle/fulle-error-dialog/error-dialog.component';

type OrderErrorHandlerContext = {
  toggleBasket?: () => void;
  openAlertProductValidity?: (data: unknown) => void;
};

@Injectable({ providedIn: 'root' })
export class OrderErrorHandlerService {
  private ctx: OrderErrorHandlerContext = {};

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private tools: ToolsService,
    private myDials: DialogsService
  ) {}

  /** à appeler depuis le composant (ngOnInit) */
  setContext(ctx: OrderErrorHandlerContext): void {
    this.ctx = ctx;
  }

  handleError(err: string | OrderError, data?: unknown): void {
    const normalized = this.normalize(err, data);
    const config = this.getConfig(normalized);

    if (!config.actions?.length) {
      this.snack(config);
      return;
    }

    const ref = this.dialog.open(ErrorDialogComponent, {
      data: { config },
      disableClose: config.type === 'error',
      panelClass: 'error-dialog',
    });

    ref.afterClosed().subscribe((result: { actionId?: ErrorActionId } | undefined) => {
      if (!result?.actionId) return;
      this.execute(result.actionId, normalized);
    });
  }

  private normalize(err: string | OrderError, data?: unknown): OrderError {
    if (typeof err === 'string') {
      // numeric / ErrorPhone
      if (!Number.isNaN(Number(err))) return { code: 'numeric', data: { raw: err } };
      if (err === 'ErrorPhone') return { code: 'ErrorPhone' };

      // autres codes
      return { code: err as OrderError['code'], data: data as never };
    }
    return err;
  }

  private getConfig(error: OrderError): ErrorConfig {
    // cas numeric / ErrorPhone
    if (error.code === 'numeric' || error.code === 'ErrorPhone') {
      return {
        titleKey: 'endOrderComponent.error',
        messageKey: 'endOrderComponent.verif',
        type: 'error',
      };
    }

    const configs: Record<string, ErrorConfig> = {
      emptypanier: {
        titleKey: 'endOrderComponent.emptyBasket',
        messageKey: 'endOrderComponent.emptyBasketMessage',
        type: 'warning',
        actions: [{ labelKey: 'common.viewBasket', id: 'OPEN_BASKET', primary: true }],
      },

      miniamount: {
        titleKey: 'endOrderComponent.minimumAmount',
        messageKey: 'endOrderComponent.minimumAmountMessage',
        type: 'warning',
        actions: [{ labelKey: 'common.understand', id: 'OPEN_MINI_AMOUNT', primary: true }],
      },

      maxweight: {
        titleKey: 'endOrderComponent.maxWeight',
        messageKey: 'endOrderComponent.maxWeightMessage',
        type: 'warning',
        actions: [{ labelKey: 'common.modify', id: 'OPEN_MAX_WEIGHT', primary: true }],
      },

      countryshipping: {
        titleKey: 'endOrderComponent.invalidCountry',
        messageKey: 'endOrderComponent.invalidCountryMessage',
        type: 'error',
        actions: [{ labelKey: 'common.modify', id: 'OPEN_INVALID_COUNTRY', primary: true }],
      },

      citydelivery: {
        titleKey: 'endOrderComponent.invalidCity',
        messageKey: 'endOrderComponent.invalidCityMessage',
        type: 'error',
        actions: [{ labelKey: 'common.modify', id: 'OPEN_INVALID_CITY', primary: true }],
      },

      invalidslot: {
        titleKey: 'endOrderComponent.invalidSlot',
        messageKey: 'endOrderComponent.invalidSlotMessage',
        type: 'warning',
        actions: [{ labelKey: 'common.selectSlot', id: 'OPEN_INVALID_SLOT', primary: true }],
      },

      invalidrub: {
        titleKey: 'endOrderComponent.invalidRub',
        messageKey: 'endOrderComponent.invalidRubMessage',
        type: 'warning',
        actions: [{ labelKey: 'common.modify', id: 'OPEN_INVALID_RUB', primary: true }],
      },

      invalidpriceperiod: {
        titleKey: 'endOrderComponent.priceChanged',
        messageKey: 'endOrderComponent.priceChangedMessage',
        type: 'warning',
        actions: [{ labelKey: 'common.viewChanges', id: 'OPEN_PRICE_PERIOD', primary: true }],
      },

      currSalemethodArchive: {
        titleKey: 'endOrderComponent.methodArchived',
        messageKey: 'endOrderComponent.methodArchivedMessage',
        type: 'error',
        actions: [{ labelKey: 'common.selectOther', id: 'OPEN_INVALID_SALEMETHOD', primary: true }],
      },

      paimentcard: {
        titleKey: 'endOrderComponent.selectPayment',
        messageKey: 'endOrderComponent.selectPaymethod',
        type: 'warning',
      },

      invalidDateproduct: {
        titleKey: 'endOrderComponent.productExpired',
        messageKey: 'endOrderComponent.productExpiredMessage',
        type: 'warning',
        actions: [{ labelKey: 'common.viewDetails', id: 'OPEN_PRODUCT_VALIDITY', primary: true }],
      },
    };

    return configs[error.code] ?? {
      titleKey: 'common.error',
      messageKey: 'common.unknownError',
      type: 'error',
    };
  }

  private execute(actionId: ErrorActionId, err: OrderError): void {
    switch (actionId) {
      case 'OPEN_BASKET':
        this.ctx.toggleBasket?.();
        return;

      case 'OPEN_MINI_AMOUNT':
        this.myDials.openMiniAmountDial();
        return;

      case 'OPEN_MAX_WEIGHT':
        if (err.code === 'maxweight' && err.data) {
          const d = err.data as any; // <- si tu veux 0 any, il faut typer deliveryAddress etc.
          this.myDials.openMaxWeightDial(d.deliveryAddress, d.isRelay, d.selectedRelay);
        }
        return;

      case 'OPEN_INVALID_COUNTRY':
        if (err.code === 'countryshipping' && err.data) {
          const d = err.data as any;
          this.myDials.openInvalidCountryDial(d.deliveryAddress);
        }
        return;

      case 'OPEN_INVALID_CITY':
        if (err.code === 'citydelivery' && err.data) {
          const d = err.data as any;
          this.myDials.openInvalidCityDial(d.deliveryAddress);
        }
        return;

      case 'OPEN_INVALID_SLOT':
        this.myDials.openInvalidSlotDial();
        return;

      case 'OPEN_INVALID_RUB':
        if (err.code === 'invalidrub') this.myDials.openAlertRubSlotDial(err.data);
        return;

      case 'OPEN_PRICE_PERIOD':
        if (err.code === 'invalidpriceperiod') this.myDials.openAlertPricePeriodDial(err.data);
        return;

      case 'OPEN_INVALID_SALEMETHOD':
        if (err.code === 'currSalemethodArchive' && err.data) {
          const d = err.data as any;
          this.myDials.openInvalidSalemethodDial(d.currSM, d.currPOS);
        }
        return;

      case 'OPEN_PRODUCT_VALIDITY':
        if (err.code === 'invalidDateproduct') this.ctx.openAlertProductValidity?.(err.data);
        return;

      case 'CLOSE':
      default:
        return;
    }
  }

  private snack(config: ErrorConfig): void {
    this.tools.openSnackBar(
      this.translate.instant(config.messageKey),
      config.type === 'error' ? 'warn' : 'info',
      4000
    );
  }
}
